import type { Ref } from "vue";
import { ref } from "vue";
import { defineStore } from "pinia";
import type { RouteLocationNormalized } from "vue-router";

export interface TagView {
    name?: string;
    path: string;
    fullPath: string;
    title: string;
    affix?: boolean;
    keepAlive?: boolean;
    query?: Record<string, any>;
    params?: Record<string, any>;
}

function toKebabCase(str: string): string {
    return str.replace(/([a-z0-9])([A-Z])/g, "$1-$2").toLowerCase();
}

export interface AdminTagsViewState {
    visitedViews: Ref<TagView[]>;
    cachedViews: Ref<string[]>;
    addView: (view: RouteLocationNormalized) => void;
    addVisitedView: (view: RouteLocationNormalized) => void;
    addCachedView: (view: RouteLocationNormalized) => void;
    getViewCacheKey: (fullPath: string) => string;
    delView: (view: TagView) => Promise<{ visitedViews: TagView[]; cachedViews: string[] }>;
    delVisitedView: (view: TagView) => Promise<TagView[]>;
    delCachedView: (view: TagView) => Promise<string[]>;
    delOthersViews: (view: TagView) => Promise<{ visitedViews: TagView[]; cachedViews: string[] }>;
    delAllViews: () => Promise<{ visitedViews: TagView[]; cachedViews: string[] }>;
    updateVisitedView: (view: RouteLocationNormalized) => void;
}

export const useAdminTagsViewStore = defineStore("adminTagsView", (): AdminTagsViewState => {
    const visitedViews = ref<TagView[]>([]);
    const cachedViews = ref<string[]>([]);
    const cacheKeyVersions = new Map<string, number>();

    const getViewCacheKey = (fullPath: string) => {
        return `${fullPath}::${cacheKeyVersions.get(fullPath) || 0}`;
    };

    const invalidateViewCacheKey = (view: Pick<TagView, "path" | "fullPath">) => {
        const fullPath = view.fullPath || view.path;
        cacheKeyVersions.set(fullPath, (cacheKeyVersions.get(fullPath) || 0) + 1);
    };

    const addVisitedView = (view: RouteLocationNormalized) => {
        if (!view.meta || !view.meta.title) return;
        const exists = visitedViews.value.some((v) => v.path === view.path);
        if (exists) {
            // 更新参数
            const index = visitedViews.value.findIndex((v) => v.path === view.path);
            if (index > -1) {
                visitedViews.value[index] = {
                    name: (view.name as string) || "",
                    path: view.path,
                    fullPath: view.fullPath,
                    title: (view.meta.title as string) || "未命名",
                    affix: !!view.meta.affix,
                    keepAlive: view.meta.keepAlive !== false,
                    query: view.query,
                    params: view.params
                };
            }
            return;
        }

        visitedViews.value.push({
            name: (view.name as string) || "",
            path: view.path,
            fullPath: view.fullPath,
            title: (view.meta.title as string) || "未命名",
            affix: !!view.meta.affix,
            keepAlive: view.meta.keepAlive !== false,
            query: view.query,
            params: view.params
        });
    };

    const addCachedView = (view: RouteLocationNormalized) => {
        const viewName = view.name as string;
        if (!viewName) return;
        // 如果 meta 中未显式禁用 keepAlive，则默认缓存
        if (view.meta && view.meta.keepAlive !== false) {
            const kebabName = toKebabCase(viewName);
            if (!cachedViews.value.includes(viewName)) {
                cachedViews.value.push(viewName);
            }
            if (kebabName !== viewName && !cachedViews.value.includes(kebabName)) {
                cachedViews.value.push(kebabName);
            }
        }
    };

    const addView = (view: RouteLocationNormalized) => {
        addVisitedView(view);
        addCachedView(view);
    };

    const delVisitedView = (view: TagView): Promise<TagView[]> => {
        return new Promise((resolve) => {
            const index = visitedViews.value.findIndex((v) => v.path === view.path);
            if (index > -1) {
                visitedViews.value.splice(index, 1);
            }
            resolve([...visitedViews.value]);
        });
    };

    const delCachedView = (view: TagView): Promise<string[]> => {
        return new Promise((resolve) => {
            invalidateViewCacheKey(view);
            const viewName = view.name;
            if (viewName) {
                const hasOtherViewWithSameName = visitedViews.value.some((item) => {
                    return item.path !== view.path && item.name === viewName && item.keepAlive !== false;
                });
                if (!hasOtherViewWithSameName) {
                    const kebabName = toKebabCase(viewName);
                    cachedViews.value = cachedViews.value.filter(
                        (name) => name !== viewName && name !== kebabName
                    );
                }
            }
            resolve([...cachedViews.value]);
        });
    };

    const delView = (view: TagView): Promise<{ visitedViews: TagView[]; cachedViews: string[] }> => {
        return new Promise((resolve) => {
            delVisitedView(view);
            delCachedView(view);
            resolve({
                visitedViews: [...visitedViews.value],
                cachedViews: [...cachedViews.value]
            });
        });
    };

    const delOthersViews = (view: TagView): Promise<{ visitedViews: TagView[]; cachedViews: string[] }> => {
        return new Promise((resolve) => {
            visitedViews.value
                .filter((item) => !item.affix && item.path !== view.path)
                .forEach(invalidateViewCacheKey);
            visitedViews.value = visitedViews.value.filter((v) => {
                return v.affix || v.path === view.path;
            });
            const viewName = view.name;
            if (viewName) {
                const kebabName = toKebabCase(viewName);
                cachedViews.value = cachedViews.value.filter(
                    (name) => name === viewName || name === kebabName
                );
            } else {
                cachedViews.value = [];
            }
            resolve({
                visitedViews: [...visitedViews.value],
                cachedViews: [...cachedViews.value]
            });
        });
    };

    const delAllViews = (): Promise<{ visitedViews: TagView[]; cachedViews: string[] }> => {
        return new Promise((resolve) => {
            visitedViews.value
                .filter((tag) => !tag.affix)
                .forEach(invalidateViewCacheKey);
            visitedViews.value = visitedViews.value.filter((tag) => tag.affix);
            cachedViews.value = [];
            resolve({
                visitedViews: [...visitedViews.value],
                cachedViews: [...cachedViews.value]
            });
        });
    };

    const updateVisitedView = (view: RouteLocationNormalized) => {
        for (let v of visitedViews.value) {
            if (v.path === view.path) {
                v = Object.assign(v, {
                    name: view.name,
                    path: view.path,
                    fullPath: view.fullPath,
                    title: view.meta.title || "未命名",
                    affix: !!view.meta.affix,
                    keepAlive: view.meta.keepAlive !== false,
                    query: view.query,
                    params: view.params
                });
                break;
            }
        }
    };

    return {
        visitedViews,
        cachedViews,
        addView,
        addVisitedView,
        addCachedView,
        getViewCacheKey,
        delView,
        delVisitedView,
        delCachedView,
        delOthersViews,
        delAllViews,
        updateVisitedView
    };
}, {
    persist: {
        paths: ["visitedViews", "cachedViews"]
    }
});
